"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { isPerformanceCriticalPath } from "@/lib/performance-critical-paths";
import { PanchangContextStripSkeleton } from "./PanchangContextStripSkeleton";

const CRITICAL_STRIP_DEFER_MS = 30000;
const STRIP_ACTIVATION_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const InteractivePanchangContextStrip = dynamic(
  () => import("@/components/PanchangContextStripInteractive"),
  {
    ssr: false,
    loading: () => <PanchangContextStripSkeleton />,
  }
);

function useDesktopPanchangStripEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 640px)");
    const update = () => setEnabled(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return enabled;
}

function useInteractiveStripReady(enabled: boolean) {
  const pathname = usePathname();
  const [ready, setReady] = useState(false);
  const isCriticalPath = isPerformanceCriticalPath(pathname || "/");

  useEffect(() => {
    if (!enabled || ready) return undefined;
    const activate = () => setReady(true);
    if (!isCriticalPath) {
      const timer = window.setTimeout(activate, 0);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(activate, CRITICAL_STRIP_DEFER_MS);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of STRIP_ACTIVATION_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }
    return () => {
      window.clearTimeout(timer);
      for (const eventName of STRIP_ACTIVATION_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [enabled, isCriticalPath, ready]);

  return ready;
}

export default function PanchangContextStripClient() {
  const enabled = useDesktopPanchangStripEnabled();
  const ready = useInteractiveStripReady(enabled);

  if (enabled && ready) {
    return <InteractivePanchangContextStrip />;
  }
  return <PanchangContextStripSkeleton />;
}
